
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/components/ui/use-toast';
import { Article as ContentArticle } from '@/components/reader-home/types';
import TopicHeader from '@/components/trending/TopicHeader';
import TopicHeatMap from '@/components/trending/TopicHeatMap';
import TopicListView from '@/components/trending/TopicListView';
import TopicDetails from '@/components/trending/TopicDetails';
import { Tag, Journalist } from '@/components/trending/types';

const TrendingTopics = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedView, setSelectedView] = useState<"visual" | "list">("visual");
  const [selectedRegion, setSelectedRegion] = useState("Global");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const [followedTopics, setFollowedTopics] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const [topics, setTopics] = useState<Tag[]>([]);
  const [relatedTopics, setRelatedTopics] = useState<Tag[]>([]);
  const [topicArticles, setTopicArticles] = useState<ContentArticle[]>([]);
  const [featuredJournalists, setFeaturedJournalists] = useState<Journalist[]>([]);

  useEffect(() => {
    const fetchTrendingTopics = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('tags')
          .select('id, name, article_count, slug, description')
          .order('article_count', { ascending: false })
          .limit(20);
        
        if (error) throw error;
        
        setTopics(data || []);
      } catch (err) {
        console.error('Error fetching trending topics:', err);
        toast({
          title: 'Error loading topics',
          description: 'Could not load trending topics. Please try again.',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    };

    const fetchFollowedTopics = async () => {
      if (!user) return;
      
      try {
        const storedFollows = localStorage.getItem(`followed_topics_${user.id}`);
        if (storedFollows) {
          setFollowedTopics(JSON.parse(storedFollows));
        }
      } catch (err) {
        console.error('Error fetching followed topics:', err);
      }
    };

    fetchTrendingTopics();
    fetchFollowedTopics();
  }, [user]);

  useEffect(() => {
    if (!selectedTopic) return;
    
    const fetchRelatedTopics = async () => {
      try {
        const currentTopic = topics.find(t => t.name === selectedTopic);
        if (!currentTopic) return;
        
        const { data, error } = await supabase
          .from('tags')
          .select('id, name, article_count, slug, description')
          .neq('name', currentTopic.name)
          .order('article_count', { ascending: false })
          .limit(10);
        
        if (error) throw error;
        
        setRelatedTopics(data || []);
      } catch (err) {
        console.error('Error fetching related topics:', err);
      }
    };
    
    const fetchTopicArticles = async () => {
      try {
        const selectedTag = topics.find(t => t.name === selectedTopic);
        if (!selectedTag) return;
        
        const { data, error } = await supabase
          .from('article_tags')
          .select(`
            article_id,
            articles(
              id, 
              title, 
              excerpt, 
              author_id, 
              published_at, 
              featured_image_url,
              header_image_url,
              reading_time
            )
          `)
          .eq('tag_id', selectedTag.id)
          .limit(6);
        
        if (error) throw error;
        
        const articleIds = data
          .filter(item => item.articles)
          .map(item => ({
            id: item.articles.id,
            title: item.articles.title,
            excerpt: item.articles.excerpt || '',
            authorId: item.articles.author_id,
            date: new Date(item.articles.published_at).toLocaleDateString(),
            image: item.articles.featured_image_url || item.articles.header_image_url || 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=500&h=350&auto=format&fit=crop',
            readTime: item.articles.reading_time || 3,
            topics: [selectedTopic],
            author: 'Loading...',
            author_avatar: '',
            hasAudio: false,
            progress: 0
          } as ContentArticle));
        
        setTopicArticles(articleIds);
        
        if (articleIds.length > 0) {
          const authorIds = [...new Set(articleIds.map(a => a.authorId))];
          
          const { data: authorsData, error: authorsError } = await supabase
            .from('profiles')
            .select('id, display_name, avatar_url')
            .in('id', authorIds);
            
          if (!authorsError && authorsData) {
            const authorMap = new Map();
            authorsData.forEach(author => {
              authorMap.set(author.id, author);
            });
            
            const updatedArticles = articleIds.map(article => {
              const author = authorMap.get(article.authorId);
              return {
                ...article,
                author: author?.display_name || 'Unknown Author',
                author_avatar: author?.avatar_url || ''
              } as ContentArticle;
            });
            
            setTopicArticles(updatedArticles);
          }
        }
      } catch (err) {
        console.error('Error fetching topic articles:', err);
      }
    };
    
    fetchRelatedTopics();
    fetchTopicArticles();
  }, [selectedTopic, topics]);

  useEffect(() => {
    if (!selectedTopic) return;
    
    const fetchJournalists = async () => {
      try {
        const selectedTag = topics.find(t => t.name === selectedTopic);
        if (!selectedTag) return;
        
        const { data: articleTagsData, error: articleTagsError } = await supabase
          .from('article_tags')
          .select('article_id, articles(author_id)')
          .eq('tag_id', selectedTag.id);
          
        if (articleTagsError) throw articleTagsError;
        
        const authorIds = [...new Set(
          articleTagsData
            .filter(item => item.articles && item.articles.author_id)
            .map(item => item.articles.author_id)
        )];
        
        if (authorIds.length === 0) return;
        
        const { data: authorProfiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, display_name, avatar_url, bio, subscriber_count, user_role')
          .in('id', authorIds)
          .eq('user_role', 'journalist')
          .order('subscriber_count', { ascending: false })
          .limit(5);
          
        if (profilesError) throw profilesError;
        
        setFeaturedJournalists(authorProfiles || []);
      } catch (err) {
        console.error('Error fetching journalists:', err);
      }
    };
    
    fetchJournalists();
  }, [selectedTopic, topics]);

  const toggleFollowTopic = (topicId: string) => {
    if (!user) {
      toast({
        title: 'Login Required',
        description: 'Please log in to follow topics',
        variant: 'default'
      });
      navigate('/auth');
      return;
    }
    
    const topic = topics.find(t => t.id === topicId);
    if (!topic) return;
    
    setFollowedTopics(prev => {
      const isFollowing = prev.includes(topicId);
      
      const newFollowed = isFollowing
        ? prev.filter(id => id !== topicId)
        : [...prev, topicId];
        
      localStorage.setItem(`followed_topics_${user.id}`, JSON.stringify(newFollowed));
      
      toast({
        title: isFollowing ? 'Topic Unfollowed' : 'Topic Followed',
        description: isFollowing 
          ? `You are no longer following ${topic.name}` 
          : `You are now following ${topic.name}`,
        variant: 'default'
      });
      
      return newFollowed;
    });
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <TopicHeader 
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        selectedRegion={selectedRegion}
        selectedView={selectedView}
        setSelectedView={setSelectedView}
      />

      {selectedView === "visual" && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Topic Heat Map</h2>
          <div className="bg-card border rounded-lg p-6 min-h-[300px]">
            <TopicHeatMap 
              topics={topics}
              loading={loading}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              setSelectedTopic={setSelectedTopic}
              toggleFollowTopic={toggleFollowTopic}
              followedTopics={followedTopics}
            />
          </div>
        </div>
      )}

      {selectedView === "list" && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Trending Topics</h2>
          <TopicListView 
            topics={topics}
            loading={loading}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            setSelectedTopic={setSelectedTopic}
            toggleFollowTopic={toggleFollowTopic}
            followedTopics={followedTopics}
          />
        </div>
      )}

      {selectedTopic && (
        <TopicDetails
          selectedTopic={selectedTopic}
          topics={topics}
          relatedTopics={relatedTopics}
          topicArticles={topicArticles}
          featuredJournalists={featuredJournalists}
          followedTopics={followedTopics}
          toggleFollowTopic={toggleFollowTopic}
          setSelectedTopic={setSelectedTopic}
        />
      )}
    </div>
  );
};

export default TrendingTopics;
